<template>
  <section id="contact" class="contact-section">
    <div class="background-elements">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
      <div class="dots"></div>
    </div>

    <div class="contact-container">
      <div class="contact-info">
        <h2 class="contact-title">Get In <span>Touch</span></h2>
        <p class="contact-subtitle">We'd love to hear from you. Let us know how we can help.</p>
        
        <div class="contact-details">
                                             
          <div class="contact-item">
            <div class="icon-wrapper">
              <i class="fas fa-envelope"></i>
            </div>
            <div class="contact-text">
              <h3>Email Us</h3>
              <p>info@eduval.net</p>
            </div>
          </div>
        </div>
      </div>
      
      <div class="contact-form-wrapper">
        <form class="contact-form" @submit.prevent="submitForm">
          <div class="form-header">
            <div class="form-icon">
              <i class="fas fa-paper-plane"></i>
            </div>
            <h3>Send us a message</h3>
          </div>
          
          <div class="input-group" :class="{ 'focus': nameActive, 'has-value': name }">
            <input 
              type="text" 
              id="name" 
              v-model="name" 
              @focus="nameActive = true" 
              @blur="nameActive = false"
              required>
            <label for="name">Your Name</label>
            <div class="input-underline"></div>
          </div>
          
          <div class="input-group" :class="{ 'focus': emailActive, 'has-value': email }">
            <input 
              type="email" 
              id="email" 
              v-model="email" 
              @focus="emailActive = true" 
              @blur="emailActive = false"
              required>
            <label for="email">Your Email</label>
            <div class="input-underline"></div>
          </div>
          
          <div class="input-group" :class="{ 'focus': subjectActive, 'has-value': subject }">
            <input 
              type="text" 
              id="subject" 
              v-model="subject" 
              @focus="subjectActive = true" 
              @blur="subjectActive = false"
              required>
            <label for="subject">Subject</label>
            <div class="input-underline"></div>
          </div>
          
          <div class="input-group" :class="{ 'focus': messageActive, 'has-value': message }">
            <textarea 
              id="message" 
              v-model="message" 
              @focus="messageActive = true" 
              @blur="messageActive = false"
              required></textarea>
            <label for="message">Your Message</label>
            <div class="input-underline"></div>
          </div>

          <div class="form-feedback">
            <div class="success-message" v-if="submitted">
              <i class="fas fa-check-circle"></i>
              <p>Thank you for your message! We will get back to you shortly.</p>
            </div>
            <div class="error-message" v-if="error">
              <i class="fas fa-exclamation-circle"></i>
              <p>{{ error }}</p>
            </div>
          </div>
          
          <button type="submit" class="submit-button" :disabled="submitting || submitted">
            <span class="button-text">{{ submitting ? 'Sending...' : submitted ? 'Sent!' : 'Send Message' }}</span>
            <span class="button-icon"><i :class="submitting ? 'fas fa-spinner fa-spin' : submitted ? 'fas fa-check' : 'fas fa-long-arrow-alt-right'"></i></span>
          </button>
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import EmailService from '../services/EmailService';

export default {
  name: 'ContactSection',
  data() {
    return {
      name: '',
      email: '',
      subject: '',
      message: '',
      nameActive: false,
      emailActive: false,
      subjectActive: false,
      messageActive: false,
      submitting: false,
      submitted: false,
      error: null
    }
  },
  methods: {
    async submitForm() {
      this.submitting = true;
      this.error = null;
      
      try {
        const emailData = {
          name: this.name,
          email: this.email,
          subject: this.subject,
          message: this.message
        };
        
        const success = await EmailService.sendEmail(emailData);
        
        if (success) {
          this.submitted = true;
          
          // Show success message briefly before resetting
          setTimeout(() => {
            // Reset the form
            this.name = '';
            this.email = '';
            this.subject = '';
            this.message = '';
            this.submitted = false;
          }, 5000);
        } else {
          this.error = 'There was a problem sending your message. Please try again later.';
        }
      } catch (err) {
        this.error = 'An unexpected error occurred. Please try again later.';
        console.error('Contact form error:', err);
      } finally {
        this.submitting = false;
      }
    }
  }
}
</script>

<style scoped>
.contact-section {
  padding: 8rem 2rem;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #2c2c2c 0%, #1a1a1a 100%);
  min-height: 700px;
}

.background-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 0;
}

.circle {
  position: absolute;
  border-radius: 50%;
  opacity: 0.05;
  background: linear-gradient(135deg, #d1c06e 0%, #e0d394 100%);
}

.circle-1 {
  width: 500px;
  height: 500px;
  top: -250px;
  right: -100px;
  animation: float 15s infinite ease-in-out;
}

.circle-2 {
  width: 300px;
  height: 300px;
  bottom: -150px;
  left: -150px;
  animation: float 12s infinite ease-in-out reverse;
}

.circle-3 {
  width: 200px;
  height: 200px;
  bottom: 20%;
  right: 15%;
  animation: float 10s infinite ease-in-out;
}

.dots {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    radial-gradient(rgba(209, 192, 110, 0.15) 2px, transparent 2px),
    radial-gradient(rgba(209, 192, 110, 0.1) 2px, transparent 2px);
  background-size: 30px 30px, 60px 60px;
  background-position: 0 0, 15px 15px;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
  }
  25% {
    transform: translate(20px, -20px) rotate(5deg);
  }
  50% {
    transform: translate(0, 30px) rotate(0deg);
  }
  75% {
    transform: translate(-20px, -10px) rotate(-5deg);
  }
}

.contact-container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  position: relative;
  z-index: 10;
}

.contact-info {
  color: white;
}

.contact-title {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.contact-title span {
  color: #d1c06e;
  position: relative;
}

.contact-title span::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 100%;
  height: 3px;
  background: #d1c06e;
}

.contact-subtitle {
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.1rem;
  margin-bottom: 3rem;
  max-width: 400px;
}

.contact-details {
  margin-top: 3rem;
}

.contact-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 2.5rem;
}

.icon-wrapper {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(209, 192, 110, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1.5rem;
  flex-shrink: 0;
  color: #d1c06e;
  font-size: 1.2rem;
  box-shadow: 0 5px 20px rgba(209, 192, 110, 0.15);
  position: relative;
  transition: all 0.3s ease;
}

.icon-wrapper::after {
  content: '';
  position: absolute;
  top: -5px;
  left: -5px;
  right: -5px;
  bottom: -5px;
  border-radius: 50%;
  border: 1px solid rgba(209, 192, 110, 0.3);
  animation: pulse 2s infinite;
}

.contact-item:hover .icon-wrapper {
  transform: translateY(-5px);
  background: rgba(209, 192, 110, 0.3);
}

.contact-text h3 {
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: #d1c06e;
}

.contact-text p {
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
}

.social-links {
  display: flex;
  gap: 1rem;
  margin-top: 3rem;
}

.social-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.social-icon:hover {
  background: #d1c06e;
  transform: translateY(-5px);
}

.contact-form-wrapper {
  position: relative;
}

.contact-form {
  background: white;
  border-radius: 15px;
  padding: 3rem;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.1);
  transform: translateY(0);
  transition: all 0.5s ease;
  position: relative;
  z-index: 5;
}

.contact-form:hover {
  transform: translateY(-10px);
  box-shadow: 0 35px 60px rgba(209, 192, 110, 0.2);
}

.form-header {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.form-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #d1c06e 0%, #e0d394 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  color: white;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.form-header h3 {
  font-size: 1.5rem;
  color: #333;
  margin: 0;
}

.input-group {
  position: relative;
  margin-bottom: 2rem;
}

.input-group input,
.input-group textarea {
  width: 100%;
  padding: 12px 0;
  font-size: 1rem;
  border: none;
  outline: none;
  background: transparent;
  color: #333;
  font-family: inherit;
}

.input-group textarea {
  min-height: 120px;
  resize: vertical;
}

.input-group label {
  position: absolute;
  left: 0;
  top: 12px;
  color: #999;
  transition: all 0.3s ease;
  pointer-events: none;
}

.input-group.focus label,
.input-group.has-value label {
  top: -20px;
  font-size: 0.8rem;
  color: #d1c06e;
  font-weight: 600;
}

.input-underline {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: #eee;
}

.input-underline::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(to right, #d1c06e, #e0d394);
  transition: width 0.3s ease;
}

.input-group.focus .input-underline::after {
  width: 100%;
}

.form-feedback {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

.success-message, .error-message {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1rem;
  border-radius: 6px;
  animation: fadeIn 0.3s ease-out;
}

.success-message {
  background-color: rgba(75, 181, 67, 0.1);
  color: #4bb543;
}

.error-message {
  background-color: rgba(244, 67, 54, 0.1);
  color: #f44336;
}

.success-message i, .error-message i {
  font-size: 1.2rem;
}

.success-message p, .error-message p {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
}

.submit-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #d1c06e 0%, #e0d394 100%);
  color: white;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(209, 192, 110, 0.3);
  position: relative;
  overflow: hidden;
  z-index: 1;
  width: 100%;
}

.submit-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 0;
  height: 100%;
  background: linear-gradient(135deg, #beab4f 0%, #d1c06e 100%);
  transition: width 0.4s ease;
  z-index: -1;
}

.submit-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(209, 192, 110, 0.4);
}

.submit-button:hover::before {
  width: 100%;
}

.button-icon {
  transition: transform 0.3s ease;
}

.submit-button:hover .button-icon {
  transform: translateX(5px);
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 0.8;
  }
  70% {
    transform: scale(1.1);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 992px) {
  .contact-container {
    grid-template-columns: 1fr;
  }
  
  .contact-info {
    margin-bottom: 2rem;
  }
}

@media (max-width: 768px) {
  .contact-section {
    padding: 5rem 1.5rem;
  }
  
  .contact-form {
    padding: 2rem;
  }
  
  .circle-1 {
    width: 300px;
    height: 300px;
  }
  
  .circle-2, .circle-3 {
    display: none;
  }
}
</style> 